import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { ArrowLeft, ChevronLeft, ChevronRight, Hand, Camera, CameraOff, Volume2, VolumeX, RefreshCw, AlertTriangle, Mic } from 'lucide-react';
import MissSpark from '@/components/MissSpark';
import VoiceChat from '@/components/VoiceChat';
import { useAudio } from '@/context/AudioContext';
import { toast } from 'sonner';
import useCamera from '@/hooks/useCamera';
import useAttentionTracker from '@/hooks/useAttentionTracker';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CountingModule = () => {
  const navigate = useNavigate();
  const { playSound } = useAudio();
  const [currentNumber, setCurrentNumber] = useState(1);
  const [sparkMood, setSparkMood] = useState('happy');
  const [sparkMessage, setSparkMessage] = useState('');
  const [detectedFingers, setDetectedFingers] = useState(0);
  const [isDetecting, setIsDetecting] = useState(false);
  const [showHighFive, setShowHighFive] = useState(false);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [voiceEnabled, setVoiceEnabled] = useState(true);
  const [attentionWarning, setAttentionWarning] = useState(null);
  const [isListening, setIsListening] = useState(false);
  
  const canvasRef = useRef(null);
  const handsRef = useRef(null);
  const audioRef = useRef(null);
  const sessionId = useRef(`counting_${Date.now()}`);

  // Camera hook
  const {
    cameras,
    selectedCamera,
    cameraOn,
    videoRef,
    refreshCameras,
    toggleCamera,
    changeCamera
  } = useCamera();

  // Attention tracking hook
  const {
    isDistracted,
    distractionType,
    attentionScore,
    faceDetected,
    resetDistraction
  } = useAttentionTracker(cameraOn, videoRef.current);

  // Play TTS audio with Web Speech fallback
  const speakText = useCallback(async (text, emotion = 'excited') => {
    if (!voiceEnabled || !text) return;
    
    try {
      setIsSpeaking(true);
      
      const response = await fetch(`${API}/tts/generate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ text, tutor: 'spark', emotion })
      });
      
      const data = await response.json();
      
      if (data.audio_url && !data.mock && audioRef.current) {
        audioRef.current.src = data.audio_url;
        await audioRef.current.play();
      } else {
        // Fallback to Web Speech Synthesis (Miss Spark - cheerful female)
        if ('speechSynthesis' in window) {
          window.speechSynthesis.cancel();
          const utterance = new SpeechSynthesisUtterance(text);
          utterance.lang = 'hi-IN';
          utterance.rate = 1.1; // Slightly faster, energetic
          utterance.pitch = 1.2; // Higher pitch, cheerful
          utterance.onend = () => setIsSpeaking(false);
          utterance.onerror = () => setIsSpeaking(false);
          window.speechSynthesis.speak(utterance);
          return;
        }
      }
    } catch (error) {
      console.error('TTS error:', error);
      // Fallback to Web Speech on error
      if ('speechSynthesis' in window) {
        window.speechSynthesis.cancel();
        const utterance = new SpeechSynthesisUtterance(text);
        utterance.lang = 'hi-IN';
        utterance.rate = 1.1;
        utterance.pitch = 1.2;
        utterance.onend = () => setIsSpeaking(false);
        window.speechSynthesis.speak(utterance);
        return;
      }
    } finally {
      setIsSpeaking(false);
    }
  }, [voiceEnabled]);

  // Handle voice chat response
  const handleVoiceResponse = useCallback((data) => {
    if (data.response) {
      setSparkMessage(data.response);
      setSparkMood(data.emotion === 'excited' ? 'excited' : data.emotion === 'encouraging' ? 'encouraging' : 'happy');
    }
  }, []);

  // Handle distraction detection
  useEffect(() => {
    if (isDistracted && distractionType && !attentionWarning) {
      handleDistraction(distractionType);
    }
  }, [isDistracted, distractionType]);

  const handleDistraction = async (type) => {
    try {
      const response = await fetch(`${API}/interaction/attention?distraction_type=${type}&session_id=${sessionId.current}`, {
        method: 'POST',
        credentials: 'include'
      });
      const data = await response.json();
      
      // Customize message for Miss Spark (more gentle)
      const sparkScoldings = {
        looking_away: [
          "Beta, idhar dekho! Miss Spark ko dekho! 👀",
          "Arre wah, kahaan dekh rahe ho? Mujhe dekho!",
          "Hey baby, focus! Mere saath counting karo!"
        ],
        yawning: [
          "Arre, neend aa gayi? Chalo ek aur number count karte hain!",
          "Ubaasi? Chalo chalo, taazi hawa lo aur wapas aao!",
          "Sleepy baby? Ek jump maaro aur phir counting karo!"
        ],
        drowsy: [
          "Aankhein kholo beta! Counting time hai!",
          "Arre, so mat jao! Miss Spark ke saath practice karo!",
          "Eyes open, baby! Let's count together!"
        ]
      };

      const messages = sparkScoldings[type] || sparkScoldings.looking_away;
      const message = messages[Math.floor(Math.random() * messages.length)];
      
      setAttentionWarning(message);
      setSparkMood('encouraging');
      playSound('wrong');
      speakText(message, 'encouraging');
      
      setTimeout(() => {
        setAttentionWarning(null);
        setSparkMood('happy');
        resetDistraction();
      }, 4000);
    } catch (error) {
      console.error('Attention API error:', error);
    }
  };

  // Number colors
  const NUMBER_COLORS = [
    'from-spark to-pink-400',
    'from-teal to-aqua',
    'from-seagreen to-teal-light',
    'from-coral to-orange-400',
    'from-sunshine to-yellow-400',
    'from-purple-400 to-pink-400',
    'from-aqua to-teal',
    'from-mint to-seagreen',
    'from-pink-400 to-spark',
    'from-teal to-seagreen',
  ];

  const colorClass = NUMBER_COLORS[(currentNumber - 1) % NUMBER_COLORS.length];

  // Initialize MediaPipe Hands when camera is on
  useEffect(() => {
    if (!cameraOn) return;

    const loadMediaPipe = async () => {
      try {
        // Load MediaPipe Hands from CDN
        const script = document.createElement('script');
        script.src = 'https://cdn.jsdelivr.net/npm/@mediapipe/hands/hands.js';
        script.async = true;
        
        script.onload = async () => {
          const cameraScript = document.createElement('script');
          cameraScript.src = 'https://cdn.jsdelivr.net/npm/@mediapipe/camera_utils/camera_utils.js';
          cameraScript.async = true;
          
          cameraScript.onload = () => {
            initializeHands();
          };
          
          document.body.appendChild(cameraScript);
        };
        
        document.body.appendChild(script);
      } catch (err) {
        console.error('MediaPipe load error:', err);
        toast.error('Could not load hand tracking');
      }
    };

    loadMediaPipe();
  }, [cameraOn]);

  const initializeHands = useCallback(async () => {
    try {
      if (!window.Hands) {
        throw new Error('Hands not loaded');
      }

      const hands = new window.Hands({
        locateFile: (file) => {
          return `https://cdn.jsdelivr.net/npm/@mediapipe/hands/${file}`;
        }
      });

      hands.setOptions({
        maxNumHands: 2,
        modelComplexity: 1,
        minDetectionConfidence: 0.5,
        minTrackingConfidence: 0.5
      });

      hands.onResults(onHandResults);
      handsRef.current = hands;

      // Start camera processing
      if (videoRef.current && window.Camera) {
        const camera = new window.Camera(videoRef.current, {
          onFrame: async () => {
            if (handsRef.current && videoRef.current) {
              await handsRef.current.send({ image: videoRef.current });
            }
          },
          width: 640,
          height: 480
        });
        
        await camera.start();
        setIsDetecting(true);
      }
    } catch (err) {
      console.error('Hands init error:', err);
    }
  }, []);

  const countFingers = (landmarks) => {
    if (!landmarks || landmarks.length === 0) return 0;
    
    let totalFingers = 0;
    
    landmarks.forEach(hand => {
      // Finger tips and their base landmarks
      const fingerTips = [8, 12, 16, 20]; // Index, Middle, Ring, Pinky tips
      const fingerBases = [6, 10, 14, 18]; // Their MCP joints
      
      // Check thumb (different logic)
      const thumbTip = hand[4];
      const thumbBase = hand[2];
      if (thumbTip.x < thumbBase.x) totalFingers++;
      
      // Check other fingers
      fingerTips.forEach((tip, i) => {
        if (hand[tip].y < hand[fingerBases[i]].y) {
          totalFingers++;
        }
      });
    });
    
    return Math.min(totalFingers, 10);
  };

  const onHandResults = useCallback((results) => {
    if (!results.multiHandLandmarks || results.multiHandLandmarks.length === 0) {
      setDetectedFingers(0);
      return;
    }

    const fingers = countFingers(results.multiHandLandmarks);
    setDetectedFingers(fingers);
    
    // Draw hand landmarks
    if (canvasRef.current && videoRef.current) {
      const ctx = canvasRef.current.getContext('2d');
      canvasRef.current.width = videoRef.current.videoWidth || 640;
      canvasRef.current.height = videoRef.current.videoHeight || 480;
      
      ctx.clearRect(0, 0, canvasRef.current.width, canvasRef.current.height);
      
      // Draw landmarks
      results.multiHandLandmarks.forEach(landmarks => {
        landmarks.forEach(point => {
          ctx.beginPath();
          ctx.arc(
            point.x * canvasRef.current.width,
            point.y * canvasRef.current.height,
            5,
            0,
            2 * Math.PI
          );
          ctx.fillStyle = '#2E8B57';
          ctx.fill();
        });
      });
    }
  }, []);

  // Fetch number content
  useEffect(() => {
    const fetchContent = async () => {
      try {
        const response = await fetch(`${API}/learn/counting/${currentNumber}`);
        const data = await response.json();
        setSparkMessage(data.prompt);
        setSparkMood('excited');
        speakText(data.prompt, 'excited');
      } catch (error) {
        const fallbackMsg = `Show me ${currentNumber} finger${currentNumber > 1 ? 's' : ''}!`;
        setSparkMessage(fallbackMsg);
        speakText(fallbackMsg, 'excited');
      }
    };
    
    fetchContent();
  }, [currentNumber, speakText]);

  // Check if correct number of fingers shown
  const checkFingers = async () => {
    const isCorrect = detectedFingers === currentNumber;
    
    try {
      await fetch(`${API}/interaction/finger-count`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          expected: currentNumber,
          detected: detectedFingers,
          correct: isCorrect,
          session_id: sessionId.current
        })
      });
    } catch (error) {
      console.error('Error recording result:', error);
    }

    if (isCorrect) {
      playSound('success');
      setSparkMood('excited');
      const successMsg = `Yaaay! You showed ${currentNumber}! High five!`;
      setSparkMessage(successMsg);
      setShowHighFive(true);
      toast.success(`Perfect! That's ${currentNumber}! 🎉`);
      speakText(successMsg, 'excited');
      
      setTimeout(() => {
        setShowHighFive(false);
        if (currentNumber < 10) {
          setCurrentNumber(prev => prev + 1);
        }
      }, 2500);
    } else {
      playSound('wrong');
      setSparkMood('encouraging');
      const tryAgainMsg = `I see ${detectedFingers} fingers. Can you show me ${currentNumber}? Try again!`;
      setSparkMessage(tryAgainMsg);
      toast.info(`Almost! Show me ${currentNumber} fingers 🤚`);
      speakText(tryAgainMsg, 'encouraging');
    }
  };

  // Cleanup
  useEffect(() => {
    return () => {
      if (videoRef.current?.srcObject) {
        videoRef.current.srcObject.getTracks().forEach(track => track.stop());
      }
    };
  }, []);

  return (
    <div className="min-h-screen relative z-10 pb-8">
      {/* Hidden Audio Element */}
      <audio 
        ref={audioRef} 
        onEnded={() => setIsSpeaking(false)}
        onError={() => setIsSpeaking(false)}
      />
      
      {/* Attention Warning Banner */}
      <AnimatePresence>
        {attentionWarning && (
          <motion.div
            initial={{ y: -100 }}
            animate={{ y: 0 }}
            exit={{ y: -100 }}
            className="fixed top-0 left-0 right-0 z-50 bg-gradient-to-r from-spark to-pink-400 text-white py-4 px-6 text-center shadow-lg"
          >
            <div className="flex items-center justify-center gap-3">
              <AlertTriangle className="w-6 h-6 animate-bounce" />
              <span className="font-heading text-lg">{attentionWarning}</span>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
      
      {/* Back Button */}
      <Button
        data-testid="back-btn"
        variant="ghost"
        onClick={() => navigate('/')}
        className="back-button bg-white/80 backdrop-blur-sm"
      >
        <ArrowLeft className="w-5 h-5 mr-2" />
        Home
      </Button>

      {/* Voice Toggle */}
      <Button
        data-testid="voice-toggle-btn"
        variant="ghost"
        onClick={() => setVoiceEnabled(!voiceEnabled)}
        className="fixed top-4 right-4 z-50 bg-white/80 backdrop-blur-sm"
      >
        {voiceEnabled ? (
          <Volume2 className="w-5 h-5 text-seagreen" />
        ) : (
          <VolumeX className="w-5 h-5 text-muted-foreground" />
        )}
      </Button>

      {/* Header */}
      <div className="pt-20 pb-8 px-6 text-center">
        <h1 className="text-3xl font-heading text-foreground mb-2">Counting 1-10 with Miss Spark!</h1>
        <div className="flex justify-center gap-2">
          {[...Array(10)].map((_, idx) => (
            <div
              key={idx}
              className={`w-3 h-3 rounded-full transition-all ${
                idx + 1 === currentNumber 
                  ? 'bg-teal scale-125' 
                  : idx + 1 < currentNumber 
                    ? 'bg-seagreen' 
                    : 'bg-muted'
              }`}
            />
          ))}
        </div>
      </div>

      <div className="container mx-auto max-w-6xl px-6">
        <div className="grid lg:grid-cols-3 gap-6">
          {/* Number Display */}
          <motion.div
            key={currentNumber}
            initial={{ scale: 0, rotate: -20 }}
            animate={{ scale: 1, rotate: 0 }}
            transition={{ type: 'spring', stiffness: 300, damping: 20 }}
            className={`learning-card bg-gradient-to-br ${colorClass} flex flex-col items-center justify-center`}
          >
            <span className="number-display text-white drop-shadow-lg number-bounce">
              {currentNumber}
            </span>
            <p className="text-white text-xl font-heading mt-4">
              Show {currentNumber} finger{currentNumber > 1 ? 's' : ''}!
            </p>
          </motion.div>

          {/* Webcam Feed */}
          <div className="flex flex-col items-center">
            <div className={`webcam-frame w-full aspect-video bg-chalkboard ${isDetecting ? 'detecting' : ''} ${attentionWarning ? 'ring-4 ring-spark' : ''}`}>
              {cameraOn ? (
                <>
                  <video
                    ref={videoRef}
                    className="w-full h-full object-cover transform scale-x-[-1]"
                    autoPlay
                    playsInline
                    muted
                  />
                  <canvas
                    ref={canvasRef}
                    className="absolute inset-0 w-full h-full object-cover transform scale-x-[-1] pointer-events-none"
                  />
                  {/* Face Detection Indicator */}
                  {cameraOn && (
                    <div className={`absolute top-2 left-2 px-2 py-1 rounded-full text-xs font-medium ${faceDetected ? 'bg-seagreen text-white' : 'bg-muted text-muted-foreground'}`}>
                      {faceDetected ? '😊 Face OK' : '🔍 No Face'}
                    </div>
                  )}
                  {/* Attention Score */}
                  {cameraOn && faceDetected && (
                    <div className="absolute top-2 right-2 px-2 py-1 rounded-full text-xs font-medium bg-white/80">
                      Attention: {Math.round(attentionScore * 100)}%
                    </div>
                  )}
                </>
              ) : (
                <div className="w-full h-full flex items-center justify-center">
                  <div className="text-center text-white">
                    <CameraOff className="w-12 h-12 mx-auto mb-2 opacity-50" />
                    <p>Camera Off</p>
                  </div>
                </div>
              )}
            </div>
            
            {/* Camera Selector */}
            {cameras.length > 1 && (
              <div className="mt-3 w-full">
                <label className="text-sm text-muted-foreground mb-1 block">Select Camera:</label>
                <select
                  data-testid="camera-select"
                  value={selectedCamera}
                  onChange={(e) => changeCamera(e.target.value)}
                  className="w-full px-3 py-2 bg-white border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-seagreen"
                >
                  {cameras.map((cam, idx) => (
                    <option key={cam.deviceId} value={cam.deviceId}>
                      {cam.label || `Camera ${idx + 1}`}
                    </option>
                  ))}
                </select>
              </div>
            )}
            
            {/* Finger Detection Display */}
            <div className="mt-4 p-4 bg-white rounded-2xl shadow-card w-full">
              <p className="text-center text-muted-foreground text-sm mb-2">Fingers Detected:</p>
              <div className="finger-indicator">
                {[...Array(10)].map((_, i) => (
                  <div
                    key={i}
                    className={`finger-dot ${i < detectedFingers ? 'detected' : ''}`}
                  />
                ))}
              </div>
              <p className="text-center text-2xl font-heading mt-2 text-seagreen">{detectedFingers}</p>
            </div>

            {/* Controls */}
            <div className="flex gap-3 mt-4 flex-wrap justify-center">
              <Button
                data-testid="camera-toggle-btn"
                variant={cameraOn ? "default" : "outline"}
                onClick={toggleCamera}
                className={cameraOn ? "bg-seagreen hover:bg-seagreen-dark" : ""}
              >
                {cameraOn ? <Camera className="w-5 h-5 mr-2" /> : <CameraOff className="w-5 h-5 mr-2" />}
                {cameraOn ? "Camera On" : "Turn On Camera"}
              </Button>
              
              {cameraOn && (
                <Button
                  data-testid="refresh-cameras-btn"
                  variant="outline"
                  onClick={refreshCameras}
                  title="Refresh camera list"
                >
                  <RefreshCw className="w-5 h-5" />
                </Button>
              )}
              
              <Button
                data-testid="check-btn"
                onClick={checkFingers}
                disabled={!cameraOn}
                className="btn-playful btn-toby"
              >
                <Hand className="w-5 h-5 mr-2" />
                Check!
              </Button>
            </div>
          </div>

          {/* Miss Spark */}
          <div className="flex flex-col items-center">
            <div className={`tutor-container spark p-4 rounded-3xl ${attentionWarning ? 'animate-pulse' : ''}`}>
              <MissSpark mood={sparkMood} size="medium" speaking={sparkMood === 'excited'} />
            </div>
            
            <AnimatePresence mode="wait">
              <motion.div
                key={sparkMessage}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                className="speech-container text-center mt-4"
              >
                <p className="text-foreground font-medium text-sm">{sparkMessage}</p>
              </motion.div>
            </AnimatePresence>

            {/* Voice Chat Section */}
            <div className="mt-4 p-4 bg-white/80 backdrop-blur-sm rounded-2xl shadow-card w-full">
              <h3 className="text-sm font-heading text-foreground mb-3 flex items-center gap-2">
                <Mic className="w-4 h-4 text-spark" />
                Voice Chat with Miss Spark
              </h3>
              <VoiceChat
                tutor="spark"
                module="counting"
                context={{ number: currentNumber }}
                sessionId={sessionId.current}
                onResponse={handleVoiceResponse}
                onListeningChange={setIsListening}
                onSpeakingChange={setIsSpeaking}
              />
            </div>
          </div>
        </div>

        {/* Navigation */}
        <div className="flex justify-center items-center gap-6 mt-8">
          <Button
            data-testid="prev-num-btn"
            variant="outline"
            size="lg"
            onClick={() => setCurrentNumber(prev => Math.max(1, prev - 1))}
            disabled={currentNumber === 1}
            className="rounded-full w-14 h-14"
          >
            <ChevronLeft className="w-6 h-6" />
          </Button>
          
          <span className="text-2xl font-heading text-foreground">
            {currentNumber} / 10
          </span>
          
          <Button
            data-testid="next-num-btn"
            size="lg"
            onClick={() => setCurrentNumber(prev => Math.min(10, prev + 1))}
            disabled={currentNumber === 10}
            className="btn-playful btn-teal rounded-full w-14 h-14"
          >
            <ChevronRight className="w-6 h-6" />
          </Button>
        </div>
      </div>

      {/* High Five Overlay */}
      <AnimatePresence>
        {showHighFive && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="high-five-overlay"
          >
            <motion.span
              initial={{ scale: 0, rotate: -30 }}
              animate={{ scale: 1, rotate: 0 }}
              exit={{ scale: 0, rotate: 30 }}
              className="high-five"
            >
              🖐️
            </motion.span>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default CountingModule;
